// How a task's date reads on the Tasks page (module 10 DESIGN "The Tasks page"; U28's clock plate). Pure, tested.

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function startOfLocalDay(t: number): number { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime(); }

export type DueKind = 'overdue' | 'today' | 'week' | 'later';

/** Where a due date falls from the person's today: overdue (before today), today, within six days, or later. */
export function dueKind(iso: string, precision: string, now: number = Date.now()): DueKind {
  const t = new Date(iso).getTime();
  const today = startOfLocalDay(now);
  if (t < today || (precision !== 'day' && t < now)) return 'overdue';
  if (t < today + 86_400_000) return 'today';
  if (t < today + 7 * 86_400_000) return 'week';
  return 'later';
}

/** "Today", "Today 17:00", "Thu 18", "Oct 1": the words a date plate carries. A time only when the time was fixed. */
export function dueWords(iso: string, precision: string, now: number = Date.now()): string {
  const d = new Date(iso);
  const time = precision === 'minute' || precision === 'hour' ? ` ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '';
  const today = startOfLocalDay(now);
  const day = startOfLocalDay(d.getTime());
  if (day === today) return `Today${time}`;
  if (day === today + 86_400_000) return `Tomorrow${time}`;
  if (Math.abs(day - today) < 7 * 86_400_000) return `${DOW[d.getDay()]} ${d.getDate()}${time}`;
  return `${MON[d.getMonth()]} ${d.getDate()}${d.getFullYear() !== new Date(now).getFullYear() ? ` ${d.getFullYear()}` : ''}${time}`;
}

/** "today", "yesterday", "3 days ago", "on Sep 19": when something already happened, in the person's own calendar. */
export function agoWords(iso: string, now: number = Date.now()): string {
  const d = new Date(iso);
  const days = Math.round((startOfLocalDay(now) - startOfLocalDay(d.getTime())) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return `on ${MON[d.getMonth()]} ${d.getDate()}${d.getFullYear() !== new Date(now).getFullYear() ? ` ${d.getFullYear()}` : ''}`;
}

/** The value a date input takes for a due date, in the person's own calendar. */
export function inputDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A date input's value as the instant a day-precision task is due: 17:00 that day where the person is. */
export function fromInputDate(v: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 17, 0).toISOString();
}

/** Minutes as the page says them: "30 min", "1 h", "1 h 30". */
export function minutesWords(n: number): string {
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60), m = n % 60;
  return m ? `${h} h ${m}` : `${h} h`;
}
